// Online participant idle watcher — the browser twin of the desktop
// core/idle_watcher.py IdleWatcher. SHARED CONTRACT (both must match):
//   - activity resets a single timer; resets throttled to >= resetThrottleMs
//   - timeout = idleTimeoutSeconds; 0 (or <0) => disabled, never fires
//   - single-shot: onIdle fires at most once per instance
//   - per-session lifecycle: start() to arm, stop() to disarm
// Online-only addition: a throttled keep-alive (onKeepAlive) so the server's
// last_activity_at stays fresh while the participant is active. The owner wires
// DOM listeners -> notifyActivity(); this class owns no DOM (keeps it testable).

import { useEffect, useRef } from "react";
import { heartbeat, type SessionHandle } from "./sessionDriver";

export interface IdleWatcherOptions {
  timeoutMs: number;            // 0 or less => disabled
  onIdle: () => void;           // fired once when timeout elapses with no activity
  onKeepAlive?: () => void;     // throttled liveness ping while active
  resetThrottleMs?: number;     // min gap between timer resets (default 1000)
  keepAliveMs?: number;         // min gap between keep-alive pings (default 45000)
  now?: () => number;           // injectable clock (default Date.now)
}

export class IdleWatcher {
  private readonly timeoutMs: number;
  private readonly onIdle: () => void;
  private readonly onKeepAlive?: () => void;
  private readonly resetThrottleMs: number;
  private readonly keepAliveMs: number;
  private readonly now: () => number;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastReset = 0;
  private lastKeepAlive = 0;
  private fired = false;
  private armed = false;

  constructor(opts: IdleWatcherOptions) {
    this.timeoutMs = opts.timeoutMs;
    this.onIdle = opts.onIdle;
    // Assign only when provided: under exactOptionalPropertyTypes, writing a
    // possibly-undefined value to the optional field is a type error. Behaviour is
    // identical (the field stays undefined when no keep-alive is supplied).
    if (opts.onKeepAlive) this.onKeepAlive = opts.onKeepAlive;
    this.resetThrottleMs = opts.resetThrottleMs ?? 1000;
    this.keepAliveMs = opts.keepAliveMs ?? 45000;
    this.now = opts.now ?? Date.now;
  }

  start(): void {
    if (this.timeoutMs <= 0 || this.fired) return; // 0 = disabled
    this.armed = true;
    const t = this.now();
    this.lastReset = t;
    this.lastKeepAlive = t;
    this.arm();
  }

  stop(): void {
    this.armed = false;
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  notifyActivity(): void {
    if (!this.armed || this.fired || this.timeoutMs <= 0) return;
    const t = this.now();
    if (t - this.lastReset >= this.resetThrottleMs) {
      this.lastReset = t;
      this.arm(); // restart the single-shot timer
    }
    if (this.onKeepAlive && t - this.lastKeepAlive >= this.keepAliveMs) {
      this.lastKeepAlive = t;
      this.onKeepAlive();
    }
  }

  private arm(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fire(), this.timeoutMs);
  }

  private fire(): void {
    if (this.fired) return;
    this.fired = true;
    this.armed = false;
    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.onIdle();
  }
}

// The DOM activity set whose events reset the watcher (the browser twin of the
// desktop's input-event set). One shared, passive listener per event.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;

// React owner for ONE IdleWatcher: it owns the DOM wiring (the class owns none)
// and uses heartbeat() as its only network call. Mount it once with the run's
// active/handle state; it arms while a run is active and tears down on exit.
//
//   active  — true only while a run is on the "running" phase
//   handle  — the live session handle (its idleTimeoutSeconds drives the timeout)
//   onIdle  — end the run as an idle timeout (the caller routes the end screen)
//
// Disabled cases (no watcher, no listeners, no heartbeat): not active, no handle,
// or idleTimeoutSeconds <= 0 (0/absent => idle disabled for this study).
export function useIdleWatcher(
  active: boolean,
  handle: SessionHandle | null,
  onIdle: () => void,
): void {
  // Hold onIdle in a ref so a new onIdle identity each render doesn't re-subscribe
  // the listeners; the effect re-runs only on active and the handle's id/timeout.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const timeoutSeconds = handle?.idleTimeoutSeconds ?? 0;
  const sessionId = handle?.sessionId ?? null;

  useEffect(() => {
    if (!active || !handle || timeoutSeconds <= 0) return; // disabled: never arm

    const w = new IdleWatcher({
      timeoutMs: timeoutSeconds * 1000,
      onIdle: () => {
        void heartbeat(handle, true); // abandon server-side (reason idle_timeout)
        onIdleRef.current();
      },
      onKeepAlive: () => {
        void heartbeat(handle); // throttled liveness ping while active
      },
    });
    w.start();

    const onActivity = () => w.notifyActivity();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      w.stop();
    };
    // Re-subscribe only when the run goes active/inactive or the session/timeout
    // changes — NOT on every render (onIdle is held in a ref above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sessionId, timeoutSeconds]);
}
